import React from "react";
import {
    Box,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { CardGiftcard as CardGiftcardIcon } from "@mui/icons-material";
import { Product } from "../../../types";
import { ProductThumbnail } from "../../../components/common/ProductThumbnail";

interface QuantityDialogProps {
    open: boolean;
    product: Product | null;
    quantity: number;
    onQuantityChange: (qty: number) => void;
    isGift: boolean;
    onIsGiftChange: (isGift: boolean) => void;
    onConfirm: () => void;
    onClose: () => void;
    inputRef?: React.RefObject<HTMLInputElement | null>;
}

export const QuantityDialog: React.FC<QuantityDialogProps> = ({
    open,
    product,
    quantity,
    onQuantityChange,
    isGift,
    onIsGiftChange,
    onConfirm,
    onClose,
    inputRef,
}) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            onConfirm();
        } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Adjust Quantity</DialogTitle>
            <DialogContent>
                {product && (
                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <ProductThumbnail
                                images={product.images}
                                alt={product.name}
                                size={48}
                            />
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                    {product.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                >
                                    {product.sku}
                                </Typography>
                            </Box>
                        </Box>
                        <TextField
                            autoFocus
                            inputRef={inputRef}
                            fullWidth
                            type="number"
                            label="Quantity"
                            value={quantity}
                            onChange={(e) =>
                                onQuantityChange(Number(e.target.value) || 1)
                            }
                            onKeyDown={handleKeyDown}
                            onFocus={(e) => e.target.select()}
                            inputProps={{
                                min: 1,
                                step: 1,
                                style: {
                                    textAlign: "center",
                                    fontSize: "1.5rem",
                                },
                                "aria-label": "Quantity",
                            }}
                            sx={{ mt: 2 }}
                        />
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    checked={isGift}
                                    onChange={(e) =>
                                        onIsGiftChange(e.target.checked)
                                    }
                                    icon={<CardGiftcardIcon />}
                                    checkedIcon={<CardGiftcardIcon color="secondary" />}
                                />
                            }
                            label="Mark as gift (free of charge)"
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="primary"
                    disabled={quantity <= 0}
                >
                    Add to Cart
                </Button>
            </DialogActions>
        </Dialog>
    );
};
